import { AddIcon, Button, Grid, GridRow, Modal, SearchInput, SpinnerIcon } from '@bp/ui-components';
import { type DocumentType, graphql } from 'client/gql';
import dayjs from 'dayjs';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { type ChangeEvent, type FC, Suspense, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { BpCard } from '../../../components/BpCard/BpCard';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { WorkMaterialDetail } from '../../../components/WorkMaterialDetail/WorkMaterialDetail';
import { CourseWorkMaterialsForm } from '../../../components/WorkMaterialsForm/CourseWorkMaterialsForm';
import { CourseWorkMaterialsTable } from '../../../components/WorkMaterialsTable/CourseWorkMaterialsTable';
import { TeachingUnitContext } from '../../../context/TeachingUnitContext';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useConfirm } from '../../../hooks/useConfirm';
import { backendApi } from '../../../utils/backendApi';
import { niceDate } from '../../../utils/dateCalculations';
import { NotificationReason } from '../../../utils/matrixClient';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';

const DeleteWorkMaterialsMutationDocument = graphql(`
  mutation BpDeleteWorkMaterialsFromCourseWorkMaterials($where: WorkMaterialWhere!) {
    deleteWorkMaterials(where: $where) {
      nodesDeleted
      relationshipsDeleted
    }
  }
`);

const CourseWorkMaterialsSubpagePageQueryDocument = graphql(`
  query CourseWorkMaterialsSubpagePageData($courseUuid: ID!) {
    workMaterials(
      where: { holder: { TeachingUnit: { group: { uuid: $courseUuid } } } }
      options: { sort: [{ updated: DESC }] }
    ) {
      uuid
      __typename
      title

      visibleFrom {
        uuid
        __typename
        date
      }
      visibleTo {
        uuid
        __typename
        date
      }
      publicFrom {
        uuid
        __typename
        date
      }
      visibleAfterVisibleTo
      ...CourseWorkMaterialsTableFields
      ...WorkMaterialDetailFields
    }
    groups(where: { uuid: $courseUuid }) {
      uuid
      __typename
      name
    }
  }
`);

type WorkMaterials = DocumentType<typeof CourseWorkMaterialsSubpagePageQueryDocument>['workMaterials'];

export const CourseWorkMaterialsSubpage: FC<{
  variant: 'add' | 'edit' | 'view' | 'table';
}> = ({ variant }) => {
  const { t } = useTranslation();
  const { courseUuid, uuid } = useParams();

  const navigate = useNavigate();
  const { pimAuthClaims } = useAuthClaims();
  // TODO use organization from course!
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const { teachingUnitUuid } = useContext(TeachingUnitContext);

  const [{ data }] = useQuery({
    query: CourseWorkMaterialsSubpagePageQueryDocument,
    variables: { courseUuid: courseUuid! },
    pause: courseUuid === undefined,
  });

  const perms = usePermissionChecker();

  const [searchValue, setSearchValue] = useState('');
  const [workmaterialModalOpen, setWorkmaterialModalOpen] = useState(false);
  const [currentWorkmaterialUuid, setCurrentWorkmaterialUuid] = useState('');

  const isGroupEditor = !!perms?.isGroupEditor({
    uuid: courseUuid ?? '',
    organization: { uuid: organizationUuid },
  });

  const materials = (data?.workMaterials ?? []).map((item) => {
    return {
      ...item,
      timeFrame: `${niceDate(item.visibleFrom?.date, 'short')} - ${niceDate(item.visibleTo?.date, 'short')}`,
    };
  });

  const activeMaterials: WorkMaterials = [];
  const pastMaterials: WorkMaterials = [];
  const futureMaterials: WorkMaterials = [];
  const day = dayjs();

  if (materials && materials.length > 0) {
    materials.forEach((material) => {
      if (
        dayjs(material.visibleFrom?.date).isSame(day, 'day') ||
        dayjs(day).isBetween(material.visibleFrom?.date, material.visibleTo?.date, 'day') ||
        dayjs(material.visibleTo?.date).isSame(day, 'day')
      ) {
        activeMaterials.push(material);
      } else if (dayjs(material.visibleFrom?.date).isBefore(day, 'day')) {
        pastMaterials.push(material);
      } else {
        futureMaterials.push(material);
      }
    });
  }

  const currentWorkMaterial = materials.find((material) => material.uuid === uuid);

  const handleWorkMaterialClick = (uuid: string) => {
    navigate(`/courses/${courseUuid}/workmaterials/${uuid}`);
  };

  const handleWorkMaterialEditClick = (uuid: string) => {
    setCurrentWorkmaterialUuid(uuid);
    setWorkmaterialModalOpen(true);
  };

  const handleAddWorkMaterial = () => {
    navigate(`/courses/${courseUuid}/workmaterials/add`);
  };

  const hasPermission = courseUuid
    ? perms?.canCreateWorkmaterial({
        uuid: courseUuid,
        organization: { uuid: organizationUuid },
      })
    : false;

  const filteredIrelevantMaterials = isGroupEditor
    ? [...futureMaterials, ...pastMaterials]
    : [...futureMaterials, ...pastMaterials].filter(
        (material) =>
          dayjs(material.publicFrom?.date).startOf('day').isBefore(dayjs(material.visibleFrom?.date).startOf('day')) ||
          material.visibleAfterVisibleTo,
      );

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('workmaterials.title'),
      context: 'neutrum',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const [, deleteWorkMaterial] = useMutation(DeleteWorkMaterialsMutationDocument);

  const handleDelete = async (uuid: string, title: string) => {
    const confirm = await confirmDelete();
    if (confirm) {
      const res = await deleteWorkMaterial({
        where: {
          uuid,
        },
      });
      if (res.error) {
        showErrorToast(res.error);
      } else {
        showSuccessToast(t('common.success'));

        void backendApi.notify({
          type: NotificationReason.CourseMaterialDeleted,
          groupUuid: courseUuid ?? '',
          subjectName: title,
          groupName: data?.groups[0]?.name ?? '',
        });
      }
    }
  };

  const handleClose = async (uuid?: string, title?: string) => {
    if (courseUuid) {
      // send matrix notification
      const notificationBody: {
        type: NotificationReason;
        subjectUuid?: string;
        groupUuid: string;
        subjectName: string;
        groupName: string;
      } = {
        type: variant === 'add' ? NotificationReason.CourseMaterialCreated : NotificationReason.CourseMaterialUpdated,
        groupUuid: courseUuid,
        subjectName: title ?? '',
        groupName: data?.groups[0]?.name ?? '',
      };

      if (variant === 'edit' && uuid) {
        notificationBody.subjectUuid = uuid;
      }

      void backendApi.notify(notificationBody);
    }
    navigate(`/courses/${courseUuid}/workmaterials/`);
  };

  return (
    <BpSubpage>
      {variant === 'add' && courseUuid && hasPermission && (
        <CourseWorkMaterialsForm onClose={handleClose} currentTeachingUnitUuid={teachingUnitUuid ?? ''} />
      )}
      {variant === 'edit' && courseUuid && hasPermission && (
        <CourseWorkMaterialsForm
          onClose={(uuid, title) => handleClose(uuid, title)}
          currentTeachingUnitUuid={teachingUnitUuid ?? ''}
          workMaterialUuid={uuid ?? undefined}
        />
      )}
      {variant === 'view' && courseUuid && currentWorkMaterial && (
        <WorkMaterialDetail
          onClose={() => navigate(`/courses/${courseUuid}/workmaterials/`)}
          data={currentWorkMaterial}
          isGroupEditor={isGroupEditor}
        />
      )}
      {variant === 'table' && (
        <Grid>
          <GridRow mobileGap='var(--grid-column-gap)'>
            <SearchInput
              name='search'
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setSearchValue(event.target.value);
              }}
              value={searchValue}
            />

            {perms?.canCreateWorkmaterial({
              uuid: courseUuid ?? '',
              organization: { uuid: organizationUuid },
            }) && (
              <Button className='ml-auto' hierarchy='tertiary' icon={<AddIcon />} onClick={handleAddWorkMaterial}>
                {t('common.add')}
              </Button>
            )}
          </GridRow>
          <GridRow>
            <BpCard noPadding header={{ headline: t('workmaterials.relevant') }}>
              <CourseWorkMaterialsTable
                data={[...activeMaterials].filter((m) =>
                  searchValue ? m.title.toLowerCase().includes(searchValue.toLowerCase()) : true,
                )}
                onDelete={(uuid, title) => handleDelete(uuid, title)}
                isGroupEditor={isGroupEditor}
                onClick={handleWorkMaterialClick}
                onEdit={handleWorkMaterialEditClick}
                emptyStateTitle={t('workmaterials.noRelevantWorkmaterials')}
                emptyStateSubtitle={
                  perms?.canCreateWorkmaterial({
                    uuid: courseUuid ?? '',
                    organization: { uuid: organizationUuid },
                  })
                    ? t('workmaterials.createHintCourse')
                    : t('workmaterials.createHintStudent')
                }
                permission={perms}
              />
            </BpCard>
          </GridRow>
          <GridRow>
            <BpCard noPadding header={{ headline: t('workmaterials.moreMaterial') }}>
              <CourseWorkMaterialsTable
                data={filteredIrelevantMaterials.filter((m) =>
                  searchValue ? m.title.toLowerCase().includes(searchValue.toLowerCase()) : true,
                )}
                onDelete={(uuid, title) => handleDelete(uuid, title)}
                isGroupEditor={isGroupEditor}
                onClick={handleWorkMaterialClick}
                onEdit={handleWorkMaterialEditClick}
                emptyStateTitle={t('workmaterials.noOtherWorkmaterials')}
                emptyStateSubtitle={
                  perms?.isGroupEditor({
                    uuid: courseUuid ?? '',
                    organization: { uuid: organizationUuid },
                  })
                    ? t('workmaterials.createHintCourse')
                    : t('workmaterials.createHintStudent')
                }
                permission={perms}
              />
            </BpCard>
          </GridRow>
        </Grid>
      )}
      {workmaterialModalOpen && (
        <Modal
          isOpen={workmaterialModalOpen}
          onRequestClose={() => setWorkmaterialModalOpen(false)}
          hideFooter
          hideHeader
          isFormModal
        >
          <Suspense fallback={<SpinnerIcon className={'svg-icon large spinning'} />}>
            <CourseWorkMaterialsForm
              workMaterialUuid={currentWorkmaterialUuid}
              currentTeachingUnitUuid={teachingUnitUuid ?? ''}
              isModal
              onClose={async (uuid, title) => {
                await handleClose(uuid, title);
                setWorkmaterialModalOpen(false);
              }}
            />
          </Suspense>
        </Modal>
      )}
      <ConfirmationDialog />
    </BpSubpage>
  );
};
